import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus, Trash2, Users, ExternalLink, Copy, Share2, Eye, FileText, FileSpreadsheet, ArrowRightLeft, Check } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fileUrl } from "@/components/ImageUpload";
import SubmissionInspector from "@/components/tournament/SubmissionInspector";
import VerifiedBadge from "@/components/VerifiedBadge";
import { categoryLabel, PAYMENT_STATUS_META } from "@/lib/tournament";
import RosterConfirm from "@/components/tournament/RosterConfirm";
import MessagePlayers from "@/components/tournament/MessagePlayers";
import TransferDialog from "@/components/tournament/TransferDialog";
import ProofBadge from "@/components/tournament/ProofBadge";
import { jsPDF } from "jspdf";
import api, { formatApiError } from "@/lib/api";

const inputCls = "rounded-xl bg-surface glow-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400";
const PAY_OPTS = ["pending", "confirmed", "paid", "paid_confirmed", "rejected"];

function PartnerBadge({ reg }) {
  if (reg.discipline !== "doubles") return null;
  const p = reg.partner;
  if (!p) return <span className="text-[10px] mono glass rounded-full px-2 py-0.5" style={{ color: "#ef4444" }}>No partner</span>;
  const meta = { pending: { l: "Partner pending", c: "#eab308" }, accepted: { l: `w/ ${p.name || p.email}`, c: "#22c55e" }, declined: { l: "Partner declined", c: "#ef4444" } }[p.status] || { l: p.status, c: "var(--text-2)" };
  return <span className="text-[10px] mono glass rounded-full px-2 py-0.5" style={{ color: meta.c }}>{meta.l}</span>;
}

export default function Roster({ tournament }) {
  const tid = tournament.tournament_id;
  const cats = tournament.categories || [];
  const [regs, setRegs] = useState(null);
  const [walkin, setWalkin] = useState({ category_id: cats[0]?.id || "", player_name: "", partner_name: "" });
  const [adding, setAdding] = useState(false);
  const [inspect, setInspect] = useState(null);
  const [transfer, setTransfer] = useState(null);
  const mutatingRef = useRef(false);

  const load = useCallback(() => {
    api.get(`/tournaments/${tid}/registrations`).then(({ data }) => setRegs(data || [])).catch(() => setRegs(false));
  }, [tid]);

  useEffect(() => {
    load();
    const t = setInterval(() => { if (!mutatingRef.current) load(); }, 2000);
    return () => clearInterval(t);
  }, [load]);

  const [confirm, setConfirm] = useState(null);
  const nameOf = (rid) => regs.find((r) => r.registration_id === rid)?.player_name || "This player";
  const askPay = (rid, status) => { if (regs.find((r) => r.registration_id === rid)?.payment_status !== status) setConfirm({ type: "status", rid, status, name: nameOf(rid) }); };
  const askRemove = (rid) => setConfirm({ type: "delete", rid, name: nameOf(rid) });
  const askPartner = (r) => setConfirm({ type: "partner", rid: r.registration_id, name: r.player_name, partner: r.partner?.name || r.partner?.email });
  const acceptPartner = async (rid) => {
    try {
      await api.post(`/tournaments/${tid}/registrations/${rid}/partner-accept`);
      toast.success("Partner invite accepted — partner emailed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not accept the invite"); }
  };
  const doConfirm = (reason) => {
    const c = confirm;
    setConfirm(null);
    if (c.type === "delete") remove(c.rid);
    else if (c.type === "partner") acceptPartner(c.rid);
    else setPay(c.rid, c.status, reason);
  };

  const setPay = async (rid, status, reason) => {
    mutatingRef.current = true;
    setRegs((prev) => prev.map((r) => (r.registration_id === rid ? { ...r, payment_status: status } : r)));
    setInspect((cur) => (cur && cur.registration_id === rid ? { ...cur, payment_status: status } : cur));
    try {
      await api.patch(`/tournaments/${tid}/registrations/${rid}`, { payment_status: status, reason: reason || null });
      toast.success(status === "rejected" ? "Registration rejected — player emailed" : "Status updated");
      if (status === "rejected") load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); load(); }
    finally { setTimeout(() => { mutatingRef.current = false; }, 800); }
  };

  const remove = async (rid) => {
    mutatingRef.current = true;
    try { await api.delete(`/registrations/${rid}`); setRegs((prev) => prev.filter((r) => r.registration_id !== rid)); toast.success("Removed"); }
    catch (e) { toast.error("Remove failed"); }
    finally { setTimeout(() => { mutatingRef.current = false; }, 800); }
  };

  const acceptWl = async (rid) => {
    mutatingRef.current = true;
    try { await api.post(`/tournaments/${tid}/registrations/${rid}/accept-waitlist`); toast.success("Accepted from waitlist — player emailed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not accept"); }
    finally { setTimeout(() => { mutatingRef.current = false; }, 800); }
  };

  const addWalkin = async () => {
    if (!walkin.category_id || !walkin.player_name.trim()) { toast.error("Pick a category and enter a name"); return; }
    setAdding(true);
    try {
      await api.post(`/tournaments/${tid}/walkin`, walkin);
      setWalkin((w) => ({ ...w, player_name: "", partner_name: "" }));
      toast.success("Walk-in added");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not add"); }
    finally { setAdding(false); }
  };

  const shareLink = `${window.location.origin}/t/${tid}`;
  const selectedCat = cats.find((c) => c.id === walkin.category_id);

  const fields = (tournament.custom_fields || []).filter((f) => f.type !== "header");
  const payLabel = (s) => (PAYMENT_STATUS_META[s] || { l: s }).l;
  const catName = (cid) => categoryLabel(cats.find((c) => c.id === cid) || {});
  const respVal = (r, f) => {
    const v = (r.custom_responses || {})[f.id];
    if (!v) return "";
    if (f.type === "image" || f.type === "file") return v.startsWith("http") ? v : `${window.location.origin}${v}`;
    return String(v);
  };

  const exportCsv = () => {
    const list = Array.isArray(regs) ? regs : [];
    const head = ["Category", "Player", "Partner", "Email", "Payment Status", ...fields.map((f) => f.label)];
    const esc = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
    const lines = [head.map(esc).join(",")];
    list.forEach((r) => {
      lines.push([catName(r.category_id), r.player_name, r.partner?.name || r.partner?.email || "", r.player_email || "", payLabel(r.payment_status), ...fields.map((f) => respVal(r, f))].map(esc).join(","));
    });
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${tournament.name}-registrations.csv`.replace(/[^a-z0-9.\-]+/gi, "_");
    a.click();
    toast.success("CSV exported");
  };

  const exportPdf = () => {
    const list = Array.isArray(regs) ? regs : [];
    const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const PW = pdf.internal.pageSize.getWidth(), PH = pdf.internal.pageSize.getHeight(), M = 42;
    let y = 118;
    pdf.setFillColor(11, 42, 74); pdf.rect(0, 0, PW, 96, "F");
    pdf.setTextColor(255, 255, 255); pdf.setFont("helvetica", "bold"); pdf.setFontSize(20);
    pdf.text(tournament.name || "Tournament", M, 44, { maxWidth: PW - M * 2 });
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(11); pdf.setTextColor(168, 230, 0);
    pdf.text(`Registration list · ${list.length} entries`, M, 66);
    pdf.setFontSize(9); pdf.setTextColor(200, 208, 220);
    pdf.text([tournament.venue, fmtHead()].filter(Boolean).join("   •   "), M, 82);
    pdf.setTextColor(30, 30, 30);
    const ensure = (h) => { if (y + h > PH - 46) { pdf.addPage(); y = M; } };
    const line = (lbl, val, bold) => {
      ensure(15); pdf.setFont("helvetica", bold ? "bold" : "normal"); pdf.setFontSize(bold ? 11 : 9.5);
      const txt = `${lbl ? lbl + ": " : ""}${val}`;
      const wrapped = pdf.splitTextToSize(txt, PW - M * 2 - 6);
      wrapped.forEach((w, i) => { if (i) ensure(13); pdf.text(w, M + (bold ? 0 : 10), y); y += 13; });
      y += 2;
    };
    cats.forEach((c) => {
      const rows = list.filter((r) => r.category_id === c.id);
      if (!rows.length) return;
      ensure(28); pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); pdf.setTextColor(11, 42, 74);
      pdf.text(`${categoryLabel(c)}  (${rows.length})`, M, y); y += 7;
      pdf.setDrawColor(168, 230, 0); pdf.setLineWidth(1); pdf.line(M, y, PW - M, y); y += 16; pdf.setTextColor(30, 30, 30);
      rows.forEach((r, i) => {
        ensure(20);
        line(`${i + 1}. ${r.player_name}${r.partner?.name ? ` / ${r.partner.name}` : ""}`, "", true);
        if (r.player_email) line("Email", r.player_email);
        line("Payment", payLabel(r.payment_status));
        fields.forEach((f) => { const v = respVal(r, f); if (v) line(f.label, v); });
        y += 6;
      });
    });
    const pages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i++) { pdf.setPage(i); pdf.setFontSize(8); pdf.setTextColor(150, 154, 164); pdf.text(`${tournament.name} · Registrations`, M, PH - 20); pdf.text(`Page ${i} of ${pages}`, PW - M, PH - 20, { align: "right" }); }
    pdf.save(`${tournament.name}-registrations.pdf`.replace(/[^a-z0-9.\-]+/gi, "_"));
    toast.success("PDF exported");
  };
  const fmtHead = () => [tournament.start_date, tournament.end_date].filter(Boolean).join(" – ");

  return (
    <div className="space-y-5">
    <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="roster-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="font-display font-bold flex items-center gap-2"><Users className="w-4 h-4 text-primary-c" /> Registrations & Roster <span className="text-xs mono" style={{ color: "var(--text-2)" }}>({Array.isArray(regs) ? regs.length : 0})</span></h3>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="roster-export-csv" onClick={exportCsv} className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5" style={{ color: "var(--text)" }}><FileSpreadsheet className="w-3.5 h-3.5 text-primary-c" /> CSV</button>
          <button data-testid="roster-export-pdf" onClick={exportPdf} className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5" style={{ color: "var(--text)" }}><FileText className="w-3.5 h-3.5 text-primary-c" /> PDF</button>
          <a href={shareLink} target="_blank" rel="noreferrer" data-testid="roster-open-portal" className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5" style={{ color: "var(--text)" }}><ExternalLink className="w-3.5 h-3.5 text-primary-c" /> Portal</a>
          <button data-testid="roster-copy-link" onClick={() => { navigator.clipboard?.writeText(shareLink); toast.success("Registration link copied"); }} className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5" style={{ color: "var(--text)" }}><Copy className="w-3.5 h-3.5 text-primary-c" /> Copy link</button>
        </div>
      </div>
      {tournament.status === "draft" && <p className="text-xs mb-3 flex items-center gap-1.5" style={{ color: "#eab308" }}><Share2 className="w-3.5 h-3.5" /> Publish the tournament (stage control above) so players can open the registration portal.</p>}

      {/* walk-in */}
      <div className="rounded-xl p-3 mb-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center" style={{ background: "var(--bg)" }} data-testid="walkin-form">
        <Select value={walkin.category_id} onValueChange={(v) => setWalkin((w) => ({ ...w, category_id: v }))}>
          <SelectTrigger data-testid="walkin-category" className="rounded-xl glow-border bg-surface" style={{ color: "var(--text)" }}><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent className="glass" style={{ background: "var(--surface-glass)" }}>
            {cats.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`walkin-cat-${c.id}`}>{categoryLabel(c)}</SelectItem>)}
          </SelectContent>
        </Select>
        <input data-testid="walkin-name" value={walkin.player_name} onChange={(e) => setWalkin((w) => ({ ...w, player_name: e.target.value }))} placeholder="Player name" className={inputCls} style={{ color: "var(--text)" }} />
        <input data-testid="walkin-partner" value={walkin.partner_name} onChange={(e) => setWalkin((w) => ({ ...w, partner_name: e.target.value }))} placeholder={selectedCat?.discipline === "doubles" ? "Partner name" : "(singles — no partner)"} disabled={selectedCat?.discipline !== "doubles"} className={`${inputCls} disabled:opacity-40`} style={{ color: "var(--text)" }} />
        <button data-testid="walkin-add" onClick={addWalkin} disabled={adding} className="btn-glow rounded-xl px-4 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-60">{adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Walk-in</button>
      </div>

      {regs === null ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary-c" /></div> :
        regs === false ? <p className="text-sm" style={{ color: "var(--text-2)" }}>Could not load roster.</p> :
        regs.length === 0 ? <p className="text-sm text-center py-8" style={{ color: "var(--text-2)" }}>No registrations yet. Share the portal link or add a walk-in.</p> : (
          <div className="space-y-4" data-testid="roster-list">
            {cats.map((c) => {
              const all = regs.filter((r) => r.category_id === c.id);
              const rows = all.filter((r) => !r.waitlisted);
              const active = rows.filter((r) => r.payment_status !== "rejected").length;
              const wl = all.filter((r) => r.waitlisted).sort((a, b) => (a.waitlisted_at || "").localeCompare(b.waitlisted_at || ""));
              if (all.length === 0) return null;
              return (
                <div key={c.id} data-testid={`roster-cat-${c.id}`}>
                  <div className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-2" style={{ color: "var(--text-2)" }}>
                    {categoryLabel(c)} · <span data-testid={`roster-count-${c.id}`}>{c.max_entries ? `${active}/${c.max_entries}` : active}</span>
                    {c.max_entries && active >= c.max_entries && <span className="text-[10px] mono rounded-full px-2 py-0.5" style={{ background: "rgba(245,158,11,0.15)", color: "#b45309" }}>FULL</span>}
                  </div>
                  <div className="space-y-2">
                    {rows.map((r) => {
                      const pm = PAYMENT_STATUS_META[r.payment_status] || PAYMENT_STATUS_META.pending;
                      return (
                        <div key={r.registration_id} data-testid={`roster-row-${r.registration_id}`} className="flex items-center justify-between gap-3 rounded-xl px-4 py-2.5 glass flex-wrap">
                          <div className="min-w-0">
                            <div className="font-bold text-sm flex items-center gap-2">
                              {r.player_name || "—"}
                              <VerifiedBadge verified={r.player_verification?.verified} testid={`roster-verified-${r.registration_id}`} />
                              {r.source === "walkin" && <span className="text-[10px] mono glass rounded-full px-2 py-0.5" style={{ color: "var(--text-2)" }}>Walk-in</span>}
                              <PartnerBadge reg={r} />
                            </div>
                            {r.player_email && <div className="text-[10px] mono" style={{ color: "var(--text-2)" }}>{r.player_email}</div>}
                            {r.last_transfer && <div className="text-[11px] mt-0.5" style={{ color: "var(--text-2)" }} data-testid={`roster-transfer-note-${r.registration_id}`}>Transferred from {catName(r.last_transfer.from)}: {r.last_transfer.reason}</div>}
                            {["flagged", "manual_review"].includes(r.proof_status) && (r.proof_check?.reasons || []).length > 0 && <div className="text-[11px] mt-0.5" style={{ color: "#eab308" }} data-testid={`roster-proof-reasons-${r.registration_id}`}>Receipt: {r.proof_check.reasons.join(" • ")}{r.proof_review_note ? ` — Player note: ${r.proof_review_note}` : ""}</div>}
                            {r.payment_status === "rejected" && <div className="text-[11px] mt-0.5" style={{ color: "#ef4444" }} data-testid={`roster-reject-reason-${r.registration_id}`}>Rejected: {r.rejection_reason}</div>}
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <ProofBadge reg={r} />
                            {r.discipline === "doubles" && r.partner?.status === "pending" && (
                              <button data-testid={`partner-accept-${r.registration_id}`} onClick={() => askPartner(r)} title="Accept the partner invite on the partner's behalf"
                                className="text-[10px] mono uppercase tracking-wider rounded-full px-2.5 py-1 flex items-center gap-1 font-bold" style={{ background: "rgba(234,179,8,0.15)", color: "#eab308" }}>
                                <Check className="w-3 h-3" /> Accept partner
                              </button>
                            )}
                            {r.user_id && (
                              <a href={`/players/${r.user_id}`} target="_blank" rel="noreferrer" data-testid={`roster-profile-${r.registration_id}`}
                                className="text-[10px] mono glass rounded-full px-2 py-1 flex items-center gap-1 text-primary-c"
                                title="Open this player's full profile (all sign-up info + Facebook + match history) in a new tab">
                                <ExternalLink className="w-3 h-3" /> Profile{r.player_verification?.verified ? " ✓" : ""}
                              </a>
                            )}
                            {r.partner?.user_id && (
                              <a href={`/players/${r.partner.user_id}`} target="_blank" rel="noreferrer" data-testid={`roster-partner-profile-${r.registration_id}`}
                                className="text-[10px] mono glass rounded-full px-2 py-1 flex items-center gap-1" style={{ color: "var(--text-2)" }}
                                title="Open the partner's full profile in a new tab">
                                <ExternalLink className="w-3 h-3" /> Partner{r.partner_verification?.verified ? " ✓" : ""}
                              </a>
                            )}
                            {r.payment_proof_url && <a href={fileUrl(r.payment_proof_url)} target="_blank" rel="noreferrer" data-testid={`proof-link-${r.registration_id}`} className="text-[10px] mono glass rounded-full px-2 py-1 flex items-center gap-1" style={{ color: "var(--primary-ink)" }}><ExternalLink className="w-3 h-3" /> Proof</a>}
                            <button data-testid={`transfer-${r.registration_id}`} onClick={() => setTransfer(r)} title="Transfer to another category" className="glass rounded-full px-2.5 py-1 text-[10px] mono uppercase tracking-wider flex items-center gap-1" style={{ color: "var(--text)" }}><ArrowRightLeft className="w-3 h-3 text-primary-c" /> Transfer</button>
                            <button data-testid={`inspect-${r.registration_id}`} onClick={() => setInspect(r)} className="glass rounded-full px-2.5 py-1 text-[10px] mono uppercase tracking-wider flex items-center gap-1" style={{ color: "var(--text)" }}><Eye className="w-3 h-3 text-primary-c" /> Inspect</button>
                            <Select value={r.payment_status} onValueChange={(v) => askPay(r.registration_id, v)}>
                              <SelectTrigger data-testid={`pay-status-${r.registration_id}`} className="rounded-full glow-border bg-surface h-8 text-xs w-40" style={{ color: pm.color }}><SelectValue /></SelectTrigger>
                              <SelectContent className="glass" style={{ background: "var(--surface-glass)" }}>
                                {PAY_OPTS.map((s) => <SelectItem key={s} value={s} data-testid={`pay-opt-${r.registration_id}-${s}`}>{PAYMENT_STATUS_META[s].l}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <button data-testid={`roster-remove-${r.registration_id}`} onClick={() => askRemove(r.registration_id)} className="w-8 h-8 rounded-full glass flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" style={{ color: "#ef4444" }} /></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {wl.length > 0 && (
                    <div className="mt-3 rounded-xl p-3" style={{ background: "rgba(245,158,11,0.08)", border: "1px dashed rgba(245,158,11,0.45)" }} data-testid={`roster-waitlist-${c.id}`}>
                      <div className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: "#b45309" }}>Waitlist · {wl.length} <span className="normal-case font-normal" style={{ color: "var(--text-2)" }}>— first in line is promoted automatically when a spot opens</span></div>
                      {wl.map((r, i) => (
                        <div key={r.registration_id} className="flex items-center gap-2 text-sm py-1.5 flex-wrap" data-testid={`waitlist-row-${r.registration_id}`}>
                          <span className="mono text-xs w-6">#{i + 1}</span>
                          <span className="font-bold">{r.player_name}</span><PartnerBadge reg={r} />
                          {r.player_email && <span className="text-[10px] mono" style={{ color: "var(--text-2)" }}>{r.player_email}</span>}
                          <span className="ml-auto flex gap-2">
                            <button data-testid={`waitlist-transfer-${r.registration_id}`} onClick={() => setTransfer(r)} className="glass rounded-full px-3 py-1 text-xs font-bold flex items-center gap-1" style={{ color: "var(--text)" }}><ArrowRightLeft className="w-3 h-3 text-primary-c" /> Transfer</button>
                            <button data-testid={`waitlist-accept-${r.registration_id}`} onClick={() => acceptWl(r.registration_id)} className="btn-glow rounded-full px-3 py-1 text-xs font-bold">Accept{c.max_entries && active >= c.max_entries ? " (over limit)" : ""}</button>
                            <button data-testid={`waitlist-remove-${r.registration_id}`} onClick={() => askRemove(r.registration_id)} className="w-7 h-7 rounded-full glass flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" style={{ color: "#ef4444" }} /></button>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      <SubmissionInspector open={!!inspect} onClose={() => setInspect(null)} reg={inspect}
        fields={tournament.custom_fields || []} onStatus={(s) => inspect && askPay(inspect.registration_id, s)} />
      <TransferDialog key={transfer?.registration_id || "none"} tid={tid} reg={transfer} cats={cats} onClose={() => setTransfer(null)} onDone={load} />
      <RosterConfirm action={confirm} onCancel={() => setConfirm(null)} onConfirm={doConfirm} />
    </div>
    <MessagePlayers tid={tid} categories={tournament.categories || []} regs={Array.isArray(regs) ? regs : []} />
    </div>
  );
}
